import type { OnInit } from '@angular/core';
import { Component, Input, signal } from '@angular/core';

/**
 * Palette per known runtime source; unknowns fall back to the neutral secondary.
 */
const PALETTE: Record<string, string> = {
  cli: 'bg-echo-blue/15 text-echo-blue',
  local: 'bg-green-500/15 text-green-400',
  'aws-dev': 'bg-star-orange/15 text-star-orange',
  'aws-stage': 'bg-yellow-500/15 text-yellow-400',
};
const NEUTRAL = 'bg-[var(--bg-secondary)] text-[var(--text-secondary)]';

function paletteFor(source: string): string {
  return PALETTE[source] ?? NEUTRAL;
}

/** Build metadata shape, shared by the API `/api` response + the SPA `/build-info.json`. */
interface BuildInfo {
  sha?: string;
  builtAt?: string;
}

/** Short git sha for the badge (full sha + build time live in the hover title). */
function shortSha(sha?: string): string {
  return sha ? sha.slice(0, 7) : '';
}

function versionTitle(label: string, v: BuildInfo): string {
  if (!v.sha && !v.builtAt) return '';
  return `${label} ${v.sha ?? ''}${v.builtAt ? ` · ${v.builtAt}` : ''}`.trim();
}

/** Compact UTC build time for the badge label (YYYY-MM-DD HH:MM); '' when unknown. */
function fmtBuilt(builtAt?: string): string {
  if (!builtAt) return '';
  const d = new Date(builtAt);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * Fixed-corner indicator that surfaces where the app is actually running:
 * the frontend serving origin (CLI dev-server / local / AWS dev) and, fetched
 * live from the BFF health endpoint, where `/api` resolves. The two can differ
 * — e.g. `serve-host` serves a CLI frontend while `/api` hits the k3d BFF — so
 * both are shown side by side. Hidden entirely in production.
 */
@Component({
  selector: 'es-env-badge',
  template: `
    @if (visible) {
      <div
        class="fixed bottom-3 left-3 z-[9999] pointer-events-none select-none flex items-center gap-1 rounded-full bg-black/40 p-1 shadow-lg ring-1 ring-black/10 backdrop-blur"
      >
        <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" [class]="fePalette" [title]="uiTitle()">
          <span class="opacity-60">UI</span>{{ frontend }}
          @if (uiSha()) { <span class="opacity-50 normal-case tracking-normal">{{ uiSha() }}</span> }
          @if (uiBuilt()) { <span class="opacity-40 normal-case tracking-normal">{{ uiBuilt() }}</span> }
        </span>
        <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" [class]="apiPalette()" [title]="apiTitle()">
          <span class="opacity-60">API</span>{{ apiSource() }}
          @if (apiSha()) { <span class="opacity-50 normal-case tracking-normal">{{ apiSha() }}</span> }
          @if (apiBuilt()) { <span class="opacity-40 normal-case tracking-normal">{{ apiBuilt() }}</span> }
        </span>
      </div>
    }
  `,
})
export class EnvBadgeComponent implements OnInit {
  /**
   * Frontend serving origin from `environment.source` (cli | local | aws-dev | aws-stage | prod).
   */
  @Input({ required: true }) frontend!: string;

  /**
   * Health endpoint to probe for the API's runtime. Same-origin `/api` by default.
   */
  @Input() apiHealthPath = '/api';

  /**
   * Where `/api` resolves, reported by the BFF; '…' until the probe returns.
   */
  protected readonly apiSource = signal<string>('…');

  /** The SPA image's own build metadata (from `/build-info.json`). */
  protected readonly uiVersion = signal<BuildInfo>({});
  /** The BFF's build metadata (from the `/api` health response's `version`). */
  protected readonly apiVersion = signal<BuildInfo>({});

  protected uiSha(): string { return shortSha(this.uiVersion().sha); }
  protected apiSha(): string { return shortSha(this.apiVersion().sha); }
  protected uiBuilt(): string { return fmtBuilt(this.uiVersion().builtAt); }
  protected apiBuilt(): string { return fmtBuilt(this.apiVersion().builtAt); }
  protected uiTitle(): string { return versionTitle('UI', this.uiVersion()); }
  protected apiTitle(): string { return versionTitle('API', this.apiVersion()); }

  protected get visible(): boolean {
    const fe = (this.frontend ?? '').toLowerCase();
    return fe !== '' && fe !== 'prod' && fe !== 'production';
  }

  protected get fePalette(): string {
    return paletteFor((this.frontend ?? '').toLowerCase());
  }

  protected apiPalette(): string {
    return paletteFor(this.apiSource().toLowerCase());
  }

  async ngOnInit(): Promise<void> {
    if (!this.visible) return;
    // UI build metadata — the SPA image's own /build-info.json (spa.Dockerfile).
    void this.loadUiVersion();
    try {
      const res = await fetch(this.apiHealthPath, { headers: { accept: 'application/json' } });
      const body = (await res.json()) as { runtime?: string; version?: BuildInfo };
      this.apiSource.set(typeof body?.runtime === 'string' ? body.runtime : 'unknown');
      if (body?.version) this.apiVersion.set(body.version);
    } catch {
      this.apiSource.set('unreachable');
    }
  }

  private async loadUiVersion(): Promise<void> {
    try {
      const res = await fetch('/build-info.json', { headers: { accept: 'application/json' } });
      if (res.ok) this.uiVersion.set((await res.json()) as BuildInfo);
    } catch {
      // No /build-info.json (e.g. `nx serve`) — leave the UI version blank.
    }
  }
}
